import { t } from "@/lib/i18n";
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { createClient } from "@/lib/connect";
import { fetchAll } from "@/lib/services/catalog";
import { FlowTemplateService } from "@gen/tracker/v1/flow_template_connect";
import {
  FlowTemplate,
  TemplateScope,
  type TemplateParameter,
} from "@gen/tracker/v1/flow_template_pb";
import { Pagination } from "@gen/tracker/v1/common_pb";
import type { FieldError } from "@gen/tracker/v1/common_pb";
import type { Flow } from "@gen/tracker/v1/flow_pb";

const client = createClient(FlowTemplateService);

export const flowTemplateKeys = {
  all: ["flow-templates"] as const,
  list: (params: ListFlowTemplatesParams) => [...flowTemplateKeys.all, "list", params] as const,
  detail: (id: string) => [...flowTemplateKeys.all, "detail", id] as const,
};

export interface ListFlowTemplatesParams {
  search?: string;
  category?: string;
}

export function useListFlowTemplates(
  params: ListFlowTemplatesParams = {},
  options?: Partial<UseQueryOptions<{ items: FlowTemplate[]; total: number }>>,
) {
  return useQuery({
    queryKey: flowTemplateKeys.list(params),
    // Every template under the filter — there are a few, and every screen
    // that lists them (the library, the pickers) lists all of them.
    queryFn: () =>
      fetchAll(async (offset, limit) => {
        const res = await client.listFlowTemplates({
          search: params.search ?? "",
          category: params.category ?? "",
          pagination: new Pagination({ limit, offset }),
        });
        return { items: res.items, total: res.pagination?.total ?? res.items.length };
      }),
    ...options,
  });
}

export function useGetFlowTemplate(id: string | undefined, options?: Partial<UseQueryOptions<FlowTemplate | undefined>>) {
  return useQuery({
    queryKey: flowTemplateKeys.detail(id ?? ""),
    enabled: !!id,
    queryFn: async () => {
      if (!id) return undefined;
      const res = await client.getFlowTemplate({ id });
      return res.template;
    },
    ...options,
  });
}

export function useCreateFlowTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (template: FlowTemplate) => {
      const res = await client.createFlowTemplate({ template });
      return res.template!;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: flowTemplateKeys.all });
    },
  });
}

export function useUpdateFlowTemplate() {
  const qc = useQueryClient();
  return useMutation({
    // Returns the full response: template + the dependent-campaign
    // propagation report (regenerated count + kept-last-good warnings).
    mutationFn: async (template: FlowTemplate) => {
      return await client.updateFlowTemplate({ template });
    },
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: flowTemplateKeys.all });
      if (res.template) {
        qc.setQueryData(flowTemplateKeys.detail(res.template.id), res.template);
      }
    },
  });
}

export function useArchiveFlowTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await client.archiveFlowTemplate({ id });
      return id;
    },
    // Optimistic remove: filter the archived row out of every cached list
    // immediately so the gallery doesn't flash. Snapshots are rolled back if
    // the BE rejects.
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: flowTemplateKeys.all });
      const snapshots = qc.getQueriesData<{ items: FlowTemplate[]; total: number }>({
        queryKey: flowTemplateKeys.all,
      });
      for (const [key, data] of snapshots) {
        if (!data) continue;
        qc.setQueryData(key, {
          ...data,
          items: data.items.filter((t) => t.id !== id),
          total: Math.max(0, data.total - 1),
        });
      }
      return { snapshots };
    },
    onError: (_err, _id, ctx) => {
      if (!ctx?.snapshots) return;
      for (const [key, data] of ctx.snapshots) {
        qc.setQueryData(key, data);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: flowTemplateKeys.all });
    },
  });
}

export function useInstantiateFlowTemplate() {
  return useMutation({
    mutationFn: async ({ templateId, bindings }: { templateId: string; bindings: Record<string, string> }): Promise<Flow> => {
      const res = await client.instantiateFlowTemplate({ templateId, bindings });
      return res.flow!;
    },
  });
}

export function templateScopeLabel(scope: TemplateScope): string {
  switch (scope) {
    case TemplateScope.WORKSPACE:
      return t("tpl.scope.ws");
    case TemplateScope.ORG:
      return t("tpl.scope.org");
    case TemplateScope.GLOBAL:
      return t("tpl.scope.global");
    default:
      return "—";
  }
}

export function paramKindLabel(kind: TemplateParameter["kind"]): string {
  switch (kind) {
    case 1:
      return t("node.offer");
    case 2:
      return t("node.lander");
    case 3:
      return t("param.text");
    case 4:
      return t("param.number");
    case 5:
      return t("cond.f.country");
    default:
      return "—";
  }
}

// useValidateFlowTemplate runs the BE ValidateFlowTemplate RPC against the
// in-flight template draft: parameter-manifest checks + the full flow
// validation suite over the blueprint with sentinel-substituted parameters.
// Blueprint errors carry the same flow-relative paths ("nodes[2].weight")
// ValidateFlow emits, so the unified editor shares one error-painting path.
export function useValidateFlowTemplate(
  template: FlowTemplate | null,
  enabled: boolean,
): { errors: FieldError[]; isLoading: boolean; refetch: () => void } {
  const query = useQuery({
    queryKey: ["flow-template-validate", template ? template.toJsonString() : null],
    enabled: enabled && !!template,
    queryFn: async () => {
      if (!template) return [] as FieldError[];
      const res = await client.validateFlowTemplate({ template });
      return res.errors;
    },
    staleTime: 1000,
  });
  return {
    errors: query.data ?? [],
    isLoading: query.isFetching,
    refetch: () => void query.refetch(),
  };
}
